import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, MapPin, Building, Loader2, Check, Search, Eye, EyeOff, Package, FileText, AlertCircle, KeyRound, RefreshCw, Lock, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCustomer } from '../context/CustomerContext';
import { lookupCep, lookupCnpj, updateCustomerProfile, verifyEmail, resendVerificationCode, requestPasswordReset, resetPassword } from '../services/api';
import { validateCpfCnpj, formatCpfCnpj, formatCEP } from '../utils/validators';

const CustomerModal = ({ isOpen, onClose, initialTab = 'login' }) => {
  const { customer, token, isLoggedIn, login, register, logout, refreshCustomer } = useCustomer();
  
  // Auth states
  const [activeTab, setActiveTab] = useState(initialTab); // 'login', 'register', 'profile', 'verify', 'forgot', 'reset'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  
  // Email verification states
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [resendingCode, setResendingCode] = useState(false);
  
  // Password reset states
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAddressFields, setShowAddressFields] = useState(false);
  
  // Form fields
  const [formData, setFormData] = useState({
    identifier: '', // For login - can be phone, email, or CPF/CNPJ
    name: '',
    phone: '',
    email: '',
    password: '',
    cpf_cnpj: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  
  // Lookup states
  const [lookingUpCep, setLookingUpCep] = useState(false);
  const [lookingUpCnpj, setLookingUpCnpj] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);

  // Set initial tab and populate form when customer logs in
  useEffect(() => {
    if (isLoggedIn && customer) {
      setActiveTab('profile');
      setFormData({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        password: '',
        cpf_cnpj: customer.cpf_cnpj || '',
        cep: customer.cep || '',
        street: customer.street || '',
        number: customer.number || '',
        complement: customer.complement || '',
        neighborhood: customer.neighborhood || '',
        city: customer.city || '',
        state: customer.state || ''
      });
    } else {
      setActiveTab(initialTab);
    }
  }, [isLoggedIn, customer, initialTab]);

  // Clear form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError('');
      setSuccess('');
      setCnpjData(null);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    
    // Validate CPF/CNPJ when it changes
    if (field === 'cpf_cnpj') {
      const digits = value.replace(/\D/g, '');
      if (digits.length === 11 || digits.length === 14) {
        const validation = validateCpfCnpj(value);
        if (!validation.valid) {
          setCpfCnpjError(`${validation.type === 'cpf' ? 'CPF' : 'CNPJ'} inválido`);
        } else {
          setCpfCnpjError('');
        }
      } else {
        setCpfCnpjError('');
      }
    }
  };

  // Format CEP as user types
  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{5})(\d)/, '$1-$2').substring(0, 9);
  };

  // Lookup CEP
  const handleCepLookup = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }

    setLookingUpCep(true);
    setError('');
    
    try {
      const data = await lookupCep(cep);
      setFormData(prev => ({
        ...prev,
        street: data.street || prev.street,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar CEP');
    } finally {
      setLookingUpCep(false);
    }
  };

  // Lookup CNPJ
  const handleCnpjLookup = async () => {
    const cnpj = formData.cpf_cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return;
    }

    setLookingUpCnpj(true);
    setError('');
    
    try {
      const data = await lookupCnpj(cnpj);
      setCnpjData(data);
      
      // Auto-fill form with CNPJ data
      setFormData(prev => ({
        ...prev,
        name: data.fantasy_name || data.name || prev.name,
        email: data.email || prev.email,
        cep: data.cep || prev.cep,
        street: data.street || prev.street,
        number: data.number || prev.number,
        complement: data.complement || prev.complement,
        neighborhood: data.neighborhood || prev.neighborhood,
        city: data.city || prev.city,
        state: data.state || prev.state
      }));
      
      setSuccess('Dados do CNPJ preenchidos automaticamente!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar CNPJ');
    } finally {
      setLookingUpCnpj(false);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await login(formData.identifier, formData.password);
      
      // Check if email verification is required
      if (result && result.email_verification_required) {
        setPendingVerificationEmail(result.email);
        setActiveTab('verify');
        setError('');
        setSuccess('Você precisa verificar seu email antes de fazer login.');
        return;
      }
      
      setSuccess('Login realizado com sucesso!');
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.email.trim()) {
      setError('Digite seu email');
      setLoading(false);
      return;
    }
    
    try {
      const result = await requestPasswordReset(formData.email);
      if (result.success) {
        setPendingVerificationEmail(formData.email.toLowerCase());
        setActiveTab('reset');
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar recuperação de senha');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!resetCode.trim() || resetCode.length !== 6) {
      setError('Digite o código de 6 dígitos');
      setLoading(false);
      return;
    }
    
    if (newPassword.length < 4) {
      setError('A nova senha deve ter pelo menos 4 caracteres');
      setLoading(false);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem');
      setLoading(false);
      return;
    }
    
    try {
      const result = await resetPassword(pendingVerificationEmail, resetCode, newPassword);
      if (result.success) {
        setSuccess(result.message);
        // Clear form and go to login
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setActiveTab('login');
          setSuccess('');
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  // Handle register
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      setLoading(false);
      return;
    }
    if (!formData.phone.trim()) {
      setError('Telefone é obrigatório');
      setLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      setError('E-mail é obrigatório');
      setLoading(false);
      return;
    }
    if (!formData.cpf_cnpj.trim()) {
      setError('CPF ou CNPJ é obrigatório');
      setLoading(false);
      return;
    }
    if (formData.password.length < 4) {
      setError('Senha deve ter pelo menos 4 caracteres');
      setLoading(false);
      return;
    }
    
    // Validate CPF/CNPJ
    const validation = validateCpfCnpj(formData.cpf_cnpj);
    if (!validation.valid) {
      setError(`${validation.type === 'cpf' ? 'CPF' : 'CNPJ'} inválido. Verifique os dígitos.`);
      setLoading(false);
      return;
    }
    
    try {
      const result = await register({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        cpf_cnpj: formData.cpf_cnpj,
        cep: formData.cep || null,
        street: formData.street || null,
        number: formData.number || null,
        complement: formData.complement || null,
        neighborhood: formData.neighborhood || null,
        city: formData.city || null,
        state: formData.state || null
      });
      
      // Check if email verification is required
      if (result.email_verification_required) {
        setPendingVerificationEmail(formData.email.toLowerCase());
        setSuccess(result.message || 'Verifique seu email para ativar a conta.');
        // Small delay to show success message, then switch to verify tab
        setTimeout(() => {
          setActiveTab('verify');
        }, 500);
      } else {
        setSuccess('Conta criada com sucesso!');
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle email verification
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError('Digite o código de 6 dígitos');
      setLoading(false);
      return;
    }
    
    try {
      await verifyEmail(pendingVerificationEmail, verificationCode);
      setSuccess('Email verificado com sucesso!');
      
      // Refresh customer data
      if (refreshCustomer) {
        await refreshCustomer();
      }
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle resend verification code
  const handleResendCode = async () => {
    setResendingCode(true);
    setError('');
    
    try {
      const result = await resendVerificationCode(pendingVerificationEmail);
      if (result.success) {
        setSuccess('Novo código enviado! Verifique seu email.');
      } else {
        setError(result.message || 'Erro ao reenviar código');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao reenviar código');
    } finally {
      setResendingCode(false);
    }
  };

  // Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const updateData = {
        name: formData.name,
        email: formData.email || null,
        cpf_cnpj: formData.cpf_cnpj || null,
        cep: formData.cep || null,
        street: formData.street || null,
        number: formData.number || null,
        complement: formData.complement || null,
        neighborhood: formData.neighborhood || null,
        city: formData.city || null,
        state: formData.state || null
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await updateCustomerProfile(token, updateData);
      setSuccess('Dados atualizados com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar dados');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    setFormData({
      name: '', phone: '', email: '', password: '',
      cpf_cnpj: '', cep: '', street: '', number: '',
      complement: '', neighborhood: '', city: '', state: ''
    });
    setActiveTab('login');
  };

  if (!isOpen) return null;

  const isCnpj = formData.cpf_cnpj.replace(/\D/g, '').length > 11;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[100] transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[90vh] bg-white rounded-2xl shadow-2xl z-[101] flex flex-col">
        {/* Header - Fixed at top */}
        <div className="bg-[#195C75] text-white p-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              {activeTab === 'verify' ? <KeyRound className="w-5 h-5" /> : 
               activeTab === 'forgot' || activeTab === 'reset' ? <Lock className="w-5 h-5" /> :
               <User className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {isLoggedIn ? 'Minha Conta' : 
                  activeTab === 'verify' ? 'Verificar Email' :
                  activeTab === 'forgot' ? 'Recuperar Senha' :
                  activeTab === 'reset' ? 'Nova Senha' :
                  activeTab === 'login' ? 'Entrar' : 'Criar Conta'}
              </h2>
              {isLoggedIn && (
                <p className="text-sm text-white/80">{customer?.name}</p>
              )}
              {activeTab === 'verify' && (
                <p className="text-sm text-white/80">Digite o código enviado</p>
              )}
              {activeTab === 'forgot' && (
                <p className="text-sm text-white/80">Vamos recuperar sua conta</p>
              )}
              {activeTab === 'reset' && (
                <p className="text-sm text-white/80">Crie uma nova senha</p>
              )}
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {/* Tabs (only show when not logged in and not verifying) */}
          {!isLoggedIn && activeTab !== 'verify' && (
            <div className="flex mb-5 bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => { setActiveTab('login'); setError(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'login' 
                    ? 'bg-white text-[#195C75] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Entrar
              </button>
              <button
                onClick={() => { setActiveTab('register'); setError(''); }}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'register' 
                    ? 'bg-white text-[#195C75] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Criar Conta
              </button>
            </div>
          )}

          {/* Email not verified warning - show when logged in but email not verified */}
          {isLoggedIn && customer && customer.email && !customer.email_verified && activeTab !== 'verify' && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-amber-800 text-sm font-medium mb-2">⚠️ Email não verificado</p>
              <p className="text-amber-700 text-xs mb-3">Verifique seu email para ter acesso completo à sua conta.</p>
              <button
                onClick={() => {
                  setPendingVerificationEmail(customer.email);
                  setActiveTab('verify');
                }}
                className="text-amber-800 text-sm font-medium underline hover:no-underline"
              >
                Verificar agora →
              </button>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </div>
          )}

          {/* LOGIN FORM */}
          {activeTab === 'login' && !isLoggedIn && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Telefone, E-mail ou CPF/CNPJ *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Telefone, e-mail ou CPF/CNPJ"
                    value={formData.identifier}
                    onChange={(e) => handleInputChange('identifier', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Use o mesmo dado que você cadastrou
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Senha *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Sua senha"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white py-2.5"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Entrar'}
              </Button>
              
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('forgot');
                    setError('');
                    setSuccess('');
                  }}
                  className="text-sm text-[#195C75] hover:underline"
                >
                  Esqueci minha senha
                </button>
              </div>
            </form>
          )}

          {/* FORGOT PASSWORD FORM */}
          {activeTab === 'forgot' && !isLoggedIn && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-semibold text-slate-800">Esqueceu sua senha?</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Digite seu email e enviaremos um código para recuperação
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  E-mail cadastrado *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white py-2.5"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enviar código'}
              </Button>
              
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('login');
                    setError('');
                    setSuccess('');
                  }}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  ← Voltar ao login
                </button>
              </div>
            </form>
          )}

          {/* RESET PASSWORD FORM */}
          {activeTab === 'reset' && !isLoggedIn && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-sm text-slate-600">
                  Digite o código enviado para:
                </p>
                <p className="font-semibold text-slate-800">{pendingVerificationEmail}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-center">
                  Código de Recuperação
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={resetCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setResetCode(value);
                  }}
                  className="text-center text-2xl tracking-[0.5em] font-bold"
                  maxLength={6}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nova senha *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 4 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Confirmar nova senha *
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite a senha novamente"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button 
                type="submit"
                disabled={loading || resetCode.length !== 6}
                className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white py-2.5"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Redefinir senha'}
              </Button>
              
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('forgot');
                    setResetCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  ← Voltar
                </button>
              </div>
            </form>
          )}

          {/* REGISTER FORM */}
          {activeTab === 'register' && !isLoggedIn && (
            <form onSubmit={handleRegister} className="space-y-3">
              {/* Row 1: Name */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Nome completo *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Seu nome ou razão social"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="pl-9 h-9 text-sm"
                    required
                  />
                </div>
              </div>
              
              {/* Row 2: Phone + Email */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Telefone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="tel"
                      placeholder="(00) 00000-0000"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="pl-9 h-9 text-sm"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">E-mail *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="pl-9 h-9 text-sm"
                      required
                    />
                  </div>
                </div>
              </div>
              
              {/* Row 3: Password + CPF/CNPJ */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Senha *</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 4 caracteres"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="pr-9 h-9 text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">CPF ou CNPJ *</label>
                  <div className="relative flex gap-1">
                    <div className="relative flex-1">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="000.000.000-00"
                        value={formData.cpf_cnpj}
                        onChange={(e) => handleInputChange('cpf_cnpj', formatCpfCnpj(e.target.value))}
                        className="pl-9 h-9 text-sm"
                        maxLength={18}
                        required
                      />
                    </div>
                    {isCnpj && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCnpjLookup}
                        disabled={lookingUpCnpj}
                        className="px-2 h-9"
                      >
                        {lookingUpCnpj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* CNPJ Info */}
              {cnpjData && (
                <div className="p-2 bg-blue-50 rounded text-xs">
                  <p className="font-medium text-blue-800">{cnpjData.name}</p>
                </div>
              )}
              
              {/* Address Section - Always visible */}
              <div className="border-t border-slate-100 pt-3">
                <label className="block text-xs font-medium text-slate-700 mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Endereço (opcional)
                </label>
                
                <div className="space-y-2">
                  {/* CEP + Street + Number */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-3">
                      <Input
                        type="text"
                        placeholder="CEP"
                        value={formData.cep}
                        onChange={(e) => handleInputChange('cep', formatCep(e.target.value))}
                        className="h-9 text-sm"
                        maxLength={9}
                      />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" variant="outline" onClick={handleCepLookup} disabled={lookingUpCep} className="w-full h-9 px-2">
                        {lookingUpCep ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                      </Button>
                    </div>
                    <div className="col-span-6">
                      <Input type="text" placeholder="Rua" value={formData.street} onChange={(e) => handleInputChange('street', e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Input type="text" placeholder="Nº" value={formData.number} onChange={(e) => handleInputChange('number', e.target.value)} className="h-9 text-sm" />
                    </div>
                  </div>
                  
                  {/* Neighborhood + City + State */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Input type="text" placeholder="Bairro" value={formData.neighborhood} onChange={(e) => handleInputChange('neighborhood', e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-4">
                      <Input type="text" placeholder="Cidade" value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="col-span-2">
                      <Input type="text" placeholder="UF" value={formData.state} onChange={(e) => handleInputChange('state', e.target.value.toUpperCase())} className="h-9 text-sm" maxLength={2} />
                    </div>
                    <div className="col-span-2">
                      <Input type="text" placeholder="Compl." value={formData.complement} onChange={(e) => handleInputChange('complement', e.target.value)} className="h-9 text-sm" />
                    </div>
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white h-10">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Criar Conta'}
              </Button>
            </form>
          )}

          {/* EMAIL VERIFICATION FORM */}
          {activeTab === 'verify' && (
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#195C75]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#195C75]" />
                </div>
                <p className="text-slate-600 text-sm">
                  Enviamos um código de 6 dígitos para:
                </p>
                <p className="font-semibold text-slate-800">{pendingVerificationEmail}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-center">
                  Código de Verificação
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                  }}
                  className="text-center text-2xl tracking-[0.5em] font-bold"
                  maxLength={6}
                  autoFocus
                />
              </div>
              
              <Button 
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full bg-[#195C75] hover:bg-[#195C75]/90 text-white py-2.5"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar'}
              </Button>
              
              <div className="text-center pt-2">
                <p className="text-sm text-slate-500 mb-2">Não recebeu o código?</p>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  className="text-[#195C75] hover:underline text-sm font-medium flex items-center gap-2 mx-auto"
                >
                  {resendingCode ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Reenviando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Reenviar código
                    </>
                  )}
                </button>
              </div>
              
              <div className="pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    if (isLoggedIn) {
                      setActiveTab('profile');
                    } else {
                      setActiveTab('register');
                    }
                    setVerificationCode('');
                    setError('');
                  }}
                  className="w-full text-slate-500 hover:text-slate-700 text-sm"
                >
                  ← {isLoggedIn ? 'Voltar ao perfil' : 'Voltar ao cadastro'}
                </button>
              </div>
            </form>
          )}

          {/* PROFILE FORM (when logged in and not verifying) */}
          {isLoggedIn && activeTab !== 'verify' && (
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              {/* Orders Link */}
              <a 
                href="/cliente"
                className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors mb-4"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-[#195C75]" />
                  <div>
                    <p className="font-medium text-slate-800">Meus Pedidos</p>
                    <p className="text-sm text-slate-500">Ver histórico de orçamentos</p>
                  </div>
                </div>
                <span className="text-[#195C75]">→</span>
              </a>
              
              {/* Basic Info */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Nome completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="tel"
                      value={formData.phone}
                      disabled
                      className="pl-10 bg-slate-50"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Telefone não pode ser alterado</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    E-mail
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Nova senha (deixe em branco para manter)
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Nova senha"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* CPF/CNPJ Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documento
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    CPF ou CNPJ
                  </label>
                  <div className="relative flex gap-2">
                    <div className="relative flex-1">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        value={formData.cpf_cnpj}
                        onChange={(e) => handleInputChange('cpf_cnpj', formatCpfCnpj(e.target.value))}
                        className="pl-10"
                        maxLength={18}
                      />
                    </div>
                    {isCnpj && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCnpjLookup}
                        disabled={lookingUpCnpj}
                        className="px-3"
                      >
                        {lookingUpCnpj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Address Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Endereço
                </h4>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">CEP</label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="00000-000"
                        value={formData.cep}
                        onChange={(e) => handleInputChange('cep', formatCep(e.target.value))}
                        className="flex-1"
                        maxLength={9}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCepLookup}
                        disabled={lookingUpCep}
                        className="px-3"
                      >
                        {lookingUpCep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Input
                        type="text"
                        placeholder="Rua"
                        value={formData.street}
                        onChange={(e) => handleInputChange('street', e.target.value)}
                      />
                    </div>
                    <Input
                      type="text"
                      placeholder="Número"
                      value={formData.number}
                      onChange={(e) => handleInputChange('number', e.target.value)}
                    />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Complemento"
                    value={formData.complement}
                    onChange={(e) => handleInputChange('complement', e.target.value)}
                  />
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="text"
                      placeholder="Bairro"
                      value={formData.neighborhood}
                      onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                    />
                    <Input
                      type="text"
                      placeholder="Cidade"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Estado (UF)"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value.toUpperCase())}
                    maxLength={2}
                    className="w-24"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#195C75] hover:bg-[#195C75]/90 text-white py-2.5"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Salvar Alterações'}
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleLogout}
                  className="px-6 text-red-600 border-red-200 hover:bg-red-50"
                >
                  Sair
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerModal;
